<script setup>
import {
  computed,
  defineProps,
  getCurrentInstance,
  ref,
  defineEmits,
  watch,
  defineExpose
} from 'vue'
import { useStore } from '@/utils/VuexHelpers'

const emit = defineEmits(['update:customer'])
const props = defineProps({
  label: {
    type: String,
    default: 'label'
  },
  customer: {
    type: [Number, Array],
    default: null
  },
  multiple: {
    type: Boolean,
    default: false
  },
  showPhoneNumber: {
    type: Boolean,
    default: true
  },
  rules: {
    type: Array,
    default: () => []
  }
})

const vm = getCurrentInstance().proxy
const store = useStore()
const customerSelectRef = ref(null)

const loadingCustomers = computed(() => store.getters.loadingCustomers)

const customerNames = computed(() => {
  const copy = JSON.parse(JSON.stringify(store.getters.customerNames))

  return copy.sort((a, b) => {
    if (a.is_active === b.is_active) {
      return a.name.localeCompare(b.name)
    }
    // Convert is_active to 1 (true) or -1 (false) and subtract to determine order
    return (b.is_active ? 1 : -1) - (a.is_active ? 1 : -1)
  })
})

const value = ref(props.customer)

const updateValue = (item) => {
  emit('update:customer', item)
}

const closeChip = (customerId) => {
  if (!props.multiple) {
    value.value = null
    updateValue(null)
    return
  }

  const updatedValue = value.value.filter((id) => id !== customerId)
  value.value = updatedValue
  updateValue(updatedValue)
}

const customFilter = (item, queryText) => {
  const textOne = item.name.toLowerCase()
  const textTwo = item.code ? item.code.toLowerCase() : ''
  const searchText = queryText.toLowerCase().trim()

  return textOne.indexOf(searchText) > -1 || textTwo.indexOf(searchText) > -1
}

const resetValidation = () => {
  customerSelectRef.value?.resetValidation()
}

defineExpose({
  resetValidation
})

if (customerNames.value.length === 0 && loadingCustomers.value !== 'names') {
  store.dispatch('fetchCustomerNames', { vm })
}

watch(
  () => props.customer,
  (newVal) => {
    if (Array.isArray(newVal) && newVal.length === 0) {
      value.value = null
      return
    }

    if (newVal === null && value.value !== null) {
      value.value = null
    }
  }
)
</script>

<template>
  <v-autocomplete
    ref="customerSelectRef"
    v-model="value"
    :items="customerNames"
    :filter="customFilter"
    :multiple="multiple"
    :label="label"
    :loading="loadingCustomers === 'names'"
    :rules="rules"
    item-text="name"
    item-value="id"
    class="mb-2"
    outlined
    dense
    chips
    small-chips
    hide-details="auto"
    @change="updateValue"
  >
    <template v-slot:selection="data">
      <v-chip
        v-bind="data.attrs"
        :input-value="data.selected"
        small
        close
        @click:close="closeChip(data.item.id)"
        class="my-1"
      >
        {{ `${data.item.code} - ${data.item.name}` }}
      </v-chip>
    </template>
    <template v-slot:item="data">
      <template v-if="typeof data.item !== 'object'">
        <v-list-item-content v-text="data.item"></v-list-item-content>
      </template>
      <template v-else>
        <v-list-item-content class="text-start">
          <v-list-item-title>
            {{ `${data.item.code} - ${data.item.name}` }}
            <span v-if="data.item.is_active !== true">
              <span class="mx-2">|</span>
              <span class="red--text text-caption">
                <font-awesome-icon
                  :icon="['fas', 'ban']"
                  class="me-1"
                />Inactive</span
              >
            </span>
          </v-list-item-title>
          <v-list-item-subtitle v-if="showPhoneNumber">{{
            data.item.phone
          }}</v-list-item-subtitle>
        </v-list-item-content>
      </template>
    </template>
  </v-autocomplete>
</template>
